import { memo } from 'react';
import { AppealHistoryItem } from '../../types/appeals';
import { AppealStatusBadge } from './AppealStatusBadge';
import { formatDistanceToNow } from 'date-fns';
import { History, ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Link } from 'react-router-dom';
import { SectionHeader } from '../ui/SectionHeader';

interface AppealHistorySectionProps {
  history: AppealHistoryItem[];
  currentAppealId: number;
  className?: string;
}

export const AppealHistorySection = memo(({ history, currentAppealId, className }: AppealHistorySectionProps) => {
  if (!history || history.length <= 1) {
    return null;
  }

  return (
    <div className={cn('glass-card p-4 md:p-5', className)}>
      <SectionHeader title="Appeal History for This Ban" icon={History} iconClassName="text-blue-400" count={history.length} />

      <div className="space-y-2">
        {history.map(item => (
          <div
            key={item.id}
            className={cn(
              'flex items-center justify-between gap-2 py-2 px-3 rounded-lg border',
              item.id === currentAppealId
                ? 'border-accent-400/30 bg-accent-500/5'
                : 'border-glass-200/10 hover:bg-glass-200/10'
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm font-mono tabular-nums text-glass-400">#{item.id}</span>
              <AppealStatusBadge status={item.status} />
              {item.verdict && (
                <span className={cn(
                  'text-xs font-semibold',
                  item.verdict === 'overturned' ? 'text-green-400' : 'text-red-400'
                )}>
                  {item.verdict}
                </span>
              )}
              {item.id === currentAppealId && (
                <span className="text-xs text-accent-300 font-medium">(current)</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-glass-400 flex-shrink-0">
              <span className="tabular-nums">{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
              {item.id !== currentAppealId && (
                <Link to={`/appeals/${item.id}`} className="btn-icon btn-icon-sm" title={`Open appeal #${item.id}`} aria-label={`Open appeal #${item.id}`}>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});
